import { useEffect, useState } from "react";

/**
 * Compute a compact countdown to the given ISO string.
 * Returns { text, expired, urgent } or null if no target.
 */
function computeRemaining(iso) {
  if (!iso) return null;
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return null;
  const now = Date.now();
  const diff = target - now;
  if (diff <= 0) return { text: "Expired", expired: true, urgent: false };
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);
  const urgent = days === 0 && hours < 6;
  let text;
  if (days >= 1) text = `${days}d ${hours}h`;
  else if (hours >= 1) text = `${hours}h ${minutes}m`;
  else text = `${minutes}m ${seconds}s`;
  return { text, expired: false, urgent };
}

export default function Countdown({ expiresAt, className = "" }) {
  const [state, setState] = useState(() => computeRemaining(expiresAt));

  useEffect(() => {
    if (!expiresAt) return undefined;
    setState(computeRemaining(expiresAt));
    // Tick every second when < 1h left, otherwise every minute
    const initial = computeRemaining(expiresAt);
    const tickMs = initial && (initial.expired || initial.text.includes("m ")) ? 1000 : 60000;
    const t = setInterval(() => setState(computeRemaining(expiresAt)), tickMs);
    return () => clearInterval(t);
  }, [expiresAt]);

  if (!state) return null;

  const base = "inline-flex items-center gap-1 font-mono text-[10px] font-bold uppercase tracking-widest px-2 py-1";
  const cls = state.expired
    ? "bg-black/70 text-white"
    : state.urgent
      ? "bg-[#E63946] text-white"
      : "bg-black text-white";

  return (
    <span className={`${base} ${cls} ${className}`} data-testid="countdown-badge">
      {state.expired ? "Expired" : `Ends in ${state.text}`}
    </span>
  );
}
